import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SearchAppBar from './Search';
import BtnComponent from '../buttonComponent/BtnComponent';
import CustomAutoComplete from './CustomAutoComplete';
import {
  FIND_DISTINCT_SERVICES,
  FIND_SERVICE_NAME_BY_BILLING_CODE,
} from '../../services/PriceTransparencyQueries/PriceTransparencyService';
import { useLazyQuery } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { getAffiliateFromDomain } from '../../shared/helper';
import { numberRegex } from '../../shared/constants';

const searchButton = {
  height: 48,
  width: '100px',
  boxSizing: 'border-box',
  fontSize: 16,
  padding: '12px 24px 12px 24px',
  mr: '16px',
};

const container = {
  position: 'absolute',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '1040px',
  p: '32px',
  pt: '10px',
  pr: '20px',
  height: '280px',
  backgroundColor: '#ffffff',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.08)',
  borderRadius: '8px',
  mt: '55px',
  zIndex: 2,
};
const secButton = {
  width: '158px',
  height: '48px',
  fontFamily: 'Inter',
  fontWeight: 500,
  fontSize: '16px',
  '&:hover': { bgcolor: 'white' },
  borderRadius: 40,
  borderColor: 'gray',
  backgroundColor: '#ffffff',
  textTransform: 'none',
  padding: '8px 16px 8px 16px',
};

const search = {
  width: '50%',
  minWidth: '440px',
  maxWidth: '500px',
};
const clearContainer = {
  display: 'flex',
  justifyContent: 'flex-end',
  cursor: 'pointer',
};

const Filters = ({
  setActive,
  active,
  handleSelectName,
  handleSelectNpiCode,
  handleSelectCode,
  name,
  setName,
  npiCode,
  setNpiCode,
  code,
  setCode,
  onFilterSubmit,
  fileCategory,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [derivedName, setDerivedName] = useState(name);
  const [derivedCode, setDerivedCode] = useState(code);
  const [derivedNpi, setDerivedNpi] = useState(npiCode);
  const serviceDebounceTimer = useRef(null);

  const [findAllServices, { data }] = useLazyQuery(FIND_DISTINCT_SERVICES, {
    fetchPolicy: 'no-cache',
  });

  const findAllService = value => {
    findAllServices({
      variables: {
        distinctServicesInput: {
          network: getAffiliateFromDomain(),
          searchText: value?.toUpperCase(),
          priceCategory: fileCategory,
        },
      },
    });
  };

  const [fetchServiceNameByBillingCode] = useLazyQuery(
    FIND_SERVICE_NAME_BY_BILLING_CODE,
    {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        onFilterSubmit(derivedName, derivedNpi, derivedCode);
        setCode(derivedCode);
        setName(derivedName);
        setNpiCode(derivedNpi);
        setActive(!active);
      },
      onError: error => {
        enqueueSnackbar(
          'No service associated with this CPT code, please enter a valid code.',
          { variant: 'error' }
        );
        setDerivedCode('');
      },
    }
  );

  const handleServiceSelection = option => {
    if (option == null) {
      setDerivedName('');
      return;
    }
    setDerivedName(option);
  };

  const handleChangeOnNpiCode = text => {
    setDerivedNpi(text);
  };

  const handleChangeOnCode = text => {
    setDerivedCode(text);
  };

  const handleClear = () => {
    setDerivedCode('');
    setDerivedNpi('');
    setDerivedName('');
    setActive(!active);
  };

  useEffect(() => {
    if (handleSelectName) {
      handleSelectName(name);
    } else if (handleSelectCode) {
      handleSelectCode(code);
    } else if (handleSelectNpiCode) {
      handleSelectNpiCode(npiCode);
    }
  }, [
    name,
    code,
    npiCode,
    handleSelectName,
    handleSelectCode,
    handleSelectNpiCode,
  ]);

  const handleClearFilterValues = () => {
    setDerivedName('');
    setDerivedCode('');
    setDerivedNpi('');
  };

  const handleChangeOnService = value => {
    setDerivedName(value);
    if (serviceDebounceTimer.current) clearTimeout(serviceDebounceTimer.current);
    if (value.trim().length >= 3) {
      serviceDebounceTimer.current = setTimeout(() => {
        findAllService(value.trim());
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (serviceDebounceTimer.current) clearTimeout(serviceDebounceTimer.current);
    };
  }, []);

  const onSearchSubmit = () => {
    if (derivedNpi !== '' && derivedCode !== '') {
      fetchServiceNameByBillingCode({
        variables: {
          reqdata: {
            billingCode: derivedCode.trim().toUpperCase(),
          },
        },
      });

      if (derivedNpi.length !== 10 || !numberRegex.test(derivedNpi)) {
        enqueueSnackbar('Please enter a valid 10 digit NPI code', {
          variant: 'error',
        });
        setDerivedNpi('');
        return;
      }
    } else if (derivedNpi !== '') {
      if (derivedNpi.length !== 10 || !numberRegex.test(derivedNpi)) {
        enqueueSnackbar('Please enter a valid 10 digit NPI code', {
          variant: 'error',
        });
        setDerivedNpi('');
        return;
      } else {
        onFilterSubmit(derivedName, derivedNpi, derivedCode);
        setCode(derivedCode);
        setName(derivedName);
        setNpiCode(derivedNpi);
        setActive(!active);
      }
    } else if (derivedCode !== '') {
      fetchServiceNameByBillingCode({
        variables: {
          reqdata: {
            billingCode: derivedCode.trim().toUpperCase(),
          },
        },
      });
    } else {
      onFilterSubmit(derivedName, derivedNpi, derivedCode);
      setCode(derivedCode);
      setName(derivedName);
      setNpiCode(derivedNpi);
      setActive(!active);
    }
  };

  return (
    <Box sx={container}>
      <Box sx={clearContainer}>
        <ClearIcon onClick={handleClear} data-testid="clearicon" />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}>
        <Box>
          <Typography variant="inputBoxTitle" data-testid="heading">
            Service name
          </Typography>
          <CustomAutoComplete
            id={'Service search placeholder'}
            inputPlaceHolder={'Search by service'}
            value={derivedName}
            clearOnBlur={false}
            onClear={() => {
              setDerivedName('');
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                handleChangeOnService(value);
              }
            }}
            onChange={(event, value) => {
              handleServiceSelection(value);
            }}
            getOptionLabel={option =>
              typeof option === 'string' ? option : option.name
            }
            options={derivedName && data?.findDistinctNameOfServices}
          />
        </Box>
        <Box component="div">
          <Typography variant="inputBoxTitle" data-testid="billingcode">
            CPT code
          </Typography>
          <SearchAppBar
            sx={search}
            value={derivedCode}
            addDebounce={false}
            onClearData={() => setDerivedCode('')}
            onSearch={handleChangeOnCode}
            placeholder="Search by CPT code"
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}>
        <Box sx={{ mt: '16px', mr: '86px' }}>
          <Typography variant="inputBoxTitle">NPI code</Typography>
          <SearchAppBar
            sx={search}
            value={derivedNpi}
            addDebounce={false}
            transformInput={val => val.replace(/[^0-9]/g, '').slice(0, 10)}
            onClearData={() => setDerivedNpi('')}
            onSearch={handleChangeOnNpiCode}
            placeholder="Search by NPI code"
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: '32px' }}>
        <BtnComponent
          sx={searchButton}
          disabled={derivedName.trim() === '' && derivedCode.trim() === '' && derivedNpi === ''}
          onClick={onSearchSubmit}
          title="Search"></BtnComponent>
        <Box>
          <Button
            sx={secButton}
            variant="outlined"
            onClick={handleClearFilterValues}>
            Clear all filters
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Filters;
